import { hbs } from 'ember-cli-htmlbars';
const __COLOCATED_TEMPLATE__ = hbs("{{! @glint-nocheck: not typesafe yet }}\n<li\n  aria-label={{t \"content_pillars.ideas.content_idea\"}}\n  class={{concat-classes \"cID--idea\" (if this.isEditing \"is--active\") \"qa--contentPillars_idea_modal\"}}\n  data-test-id=\"content-idea-card\"\n  tabindex=\"0\"\n>\n  {{#if (or this.isEditing this.cancelEdit.isRunning)}}\n    <Planner::ContentPillars::ContentIdeas::Card::Edit\n      @cancelEdit={{this.cancelEdit}}\n      @saveContentIdea={{this.saveContentIdea}}\n      @title={{this.contentIdeaChangeset.title}}\n      @titleError={{this.titleError}}\n    />\n  {{else if @contentIdea.title}}\n    <div class=\"cID--idea__body\">\n      <Shared::Ui::Button\n        @class=\"u--p__lr__0\"\n        @onClick={{toggle-action \"isEditing\" this}}\n        @variant=\"unstyled\"\n        data-test-id=\"content-idea-card-edit-button\"\n        tabindex=\"0\"\n        type=\"button\"\n      >\n        <p class=\"cID--idea__text\">{{this.contentIdeaChangeset.title}}</p>\n        <span class=\"u--screenReaderOnly\">{{t \"content_pillars.a11y.press_to_edit\"}}</span>\n      </Shared::Ui::Button>\n    </div>\n    <div class=\"cID--idea__footer\">\n      <Planner::ContentPillars::ContentIdeas::Card::Actions\n        @contentIdea={{@contentIdea}}\n        @copyIdeaToClipboard={{this.copyIdeaToClipboard}}\n        @createDraftFromIdea={{this.createDraftFromIdea}}\n        @hideContentIdea={{this.hideContentIdea}}\n      />\n    </div>\n  {{else if (or this.didReceivePlatformError this.manualFailure)}}\n    <Planner::ContentPillars::ContentIdeas::Card::Error @deleteContentIdea={{this.deleteContentIdea}} />\n  {{else}}\n    <Planner::ContentPillars::ContentIdeas::Card::Loading />\n  {{/if}}\n</li>\n\n{{#if this.displayDraftCreationModal}}\n  <Planner::ContentPillars::ContentIdeas::DraftCreationModal\n    @close={{toggle-action \"displayDraftCreationModal\" this}}\n    @contentIdea={{@contentIdea}}\n  />\n{{/if}}", {"contents":"{{! @glint-nocheck: not typesafe yet }}\n<li\n  aria-label={{t \"content_pillars.ideas.content_idea\"}}\n  class={{concat-classes \"cID--idea\" (if this.isEditing \"is--active\") \"qa--contentPillars_idea_modal\"}}\n  data-test-id=\"content-idea-card\"\n  tabindex=\"0\"\n>\n  {{#if (or this.isEditing this.cancelEdit.isRunning)}}\n    <Planner::ContentPillars::ContentIdeas::Card::Edit\n      @cancelEdit={{this.cancelEdit}}\n      @saveContentIdea={{this.saveContentIdea}}\n      @title={{this.contentIdeaChangeset.title}}\n      @titleError={{this.titleError}}\n    />\n  {{else if @contentIdea.title}}\n    <div class=\"cID--idea__body\">\n      <Shared::Ui::Button\n        @class=\"u--p__lr__0\"\n        @onClick={{toggle-action \"isEditing\" this}}\n        @variant=\"unstyled\"\n        data-test-id=\"content-idea-card-edit-button\"\n        tabindex=\"0\"\n        type=\"button\"\n      >\n        <p class=\"cID--idea__text\">{{this.contentIdeaChangeset.title}}</p>\n        <span class=\"u--screenReaderOnly\">{{t \"content_pillars.a11y.press_to_edit\"}}</span>\n      </Shared::Ui::Button>\n    </div>\n    <div class=\"cID--idea__footer\">\n      <Planner::ContentPillars::ContentIdeas::Card::Actions\n        @contentIdea={{@contentIdea}}\n        @copyIdeaToClipboard={{this.copyIdeaToClipboard}}\n        @createDraftFromIdea={{this.createDraftFromIdea}}\n        @hideContentIdea={{this.hideContentIdea}}\n      />\n    </div>\n  {{else if (or this.didReceivePlatformError this.manualFailure)}}\n    <Planner::ContentPillars::ContentIdeas::Card::Error @deleteContentIdea={{this.deleteContentIdea}} />\n  {{else}}\n    <Planner::ContentPillars::ContentIdeas::Card::Loading />\n  {{/if}}\n</li>\n\n{{#if this.displayDraftCreationModal}}\n  <Planner::ContentPillars::ContentIdeas::DraftCreationModal\n    @close={{toggle-action \"displayDraftCreationModal\" this}}\n    @contentIdea={{@contentIdea}}\n  />\n{{/if}}","moduleName":"later/components/planner/content-pillars/content-ideas/card.hbs","parseOptions":{"srcName":"later/components/planner/content-pillars/content-ideas/card.hbs"}});
import { action } from '@ember/object';
import { cancel, later } from '@ember/runloop';
import { inject as service } from '@ember/service';
import { isTesting } from '@embroider/macros';
import Component from '@glimmer/component';
import { Changeset } from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import { task } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { tracked } from 'tracked-built-ins';

import { SegmentEventTypes } from 'later/utils/constants/segment-events';
import ContentIdeaValidations from 'later/validations/content-idea';

import type { EmberRunTimer } from '@ember/runloop/types';
import type { EmberChangeset } from 'ember-changeset';
import type { TaskGenerator } from 'ember-concurrency';
import type IntlService from 'ember-intl/services/intl';
import type ContentIdeaModel from 'later/models/content-idea';
import type AlertsService from 'later/services/alerts';
import type AuthService from 'later/services/auth';
import type CacheService from 'later/services/cache';
import type DialogManagerService from 'later/services/dialog-manager';
import type ErrorsService from 'later/services/errors';
import type SegmentService from 'later/services/segment';
import type { Maybe } from 'shared/types';
import type { ValidationErr } from 'validated-changeset/dist/types';

interface CardComponentArgs {
  contentIdea: ContentIdeaModel;
  shouldInitializeEditor: (id: string) => boolean;
}

const TITLE: Readonly<string> = 'title';

export default class CardComponent extends Component<CardComponentArgs> {
  @service declare auth: AuthService;
  @service declare alerts: AlertsService;
  @service declare cache: CacheService;
  @service declare dialogManager: DialogManagerService;
  @service declare errors: ErrorsService;
  @service declare intl: IntlService;
  @service declare segment: SegmentService;

  timers: EmberRunTimer[] = [];

  @tracked displayDraftCreationModal = false;
  @tracked isEditing = false;
  @tracked manualFailure = false;

  contentIdeaChangeset: EmberChangeset;

  constructor(owner: unknown, args: CardComponentArgs) {
    super(owner, args);

    if (this.args.shouldInitializeEditor(this.args.contentIdea.id)) {
      this.isEditing = true;
    }

    this.#performManualReload(30000);
    this.#performManualReload(60000);
    this.#setManualFailure(61000);

    this.contentIdeaChangeset = Changeset(
      this.args.contentIdea,
      lookupValidator(ContentIdeaValidations),
      ContentIdeaValidations
    );
  }

  willDestroy(): void {
    super.willDestroy();
    this.timers.forEach((timer: EmberRunTimer) => cancel(timer));
    // Note: Delete any records that have not been saved
    if (!isTesting() && this.args.contentIdea?.get('isNew')) {
      this.args.contentIdea.deleteRecord();
    }
  }

  get didReceivePlatformError(): boolean {
    return Boolean(this.args.contentIdea.platformError && !this.args.contentIdea.title);
  }

  get ideaNotGenerated(): boolean {
    return !this.args.contentIdea.title && !this.args.contentIdea.generatedTitle;
  }

  get titleError(): Maybe<ValidationErr> {
    const titleError = this.contentIdeaChangeset?.errors?.filter((error) => error.key === TITLE);
    return titleError?.firstObject?.validation?.[0];
  }

  #performManualReload(timeout: number): void {
    const timer = later(
      this,
      () => {
        if (this.ideaNotGenerated) {
          this.args.contentIdea.reload();
        }
      },
      timeout
    );
    this.timers.push(timer);
  }

  #setManualFailure(timeout: number): void {
    const timer = later(
      this,
      () => {
        if (this.ideaNotGenerated && !this.didReceivePlatformError) {
          this.segment.track(SegmentEventTypes.AiGenerationTimeout, {
            model: 'content idea',
            id: this.args.contentIdea.id
          });
        }

        if (this.ideaNotGenerated) {
          this.manualFailure = true;
        }
      },
      timeout
    );
    this.timers.push(timer);
  }

  @action
  copyIdeaToClipboard(): void {
    navigator.clipboard.writeText(this.args.contentIdea.title);
    this.alerts.success(this.intl.t('content_pillars.ideas.copied_to_clipboard_secondary'), {
      title: this.intl.t('content_pillars.ideas.copied_to_clipboard')
    });
    this.segment.track(SegmentEventTypes.CopiedContentIdea, {
      best_describes: this.auth.currentAccount.bestDescribes ?? '',
      content_idea_id: this.args.contentIdea.id
    });
  }

  @action
  createDraftFromIdea(): void {
    this.displayDraftCreationModal = true;
  }

  @action
  editContentIdea(): void {
    this.isEditing = true;
  }

  @task
  *cancelEdit(): TaskGenerator<void> {
    if (!this.args.contentIdea.title) {
      yield taskFor(this.hideContentIdea).perform();
      this.isEditing = true;
    } else {
      this.contentIdeaChangeset.rollback();
      this.isEditing = false;
    }
  }

  @task
  *deleteContentIdea(): TaskGenerator<void> {
    try {
      this.args.contentIdea.deleteRecord();
      yield this.args.contentIdea.save();
    } catch (error) {
      this.errors.log('deleteContentIdea Error', error);
    }
  }

  @task
  *hideContentIdea(): TaskGenerator<void> {
    try {
      const didConfirm = yield this.dialogManager.confirmation(
        this.intl.t('content_pillars.ideas.discard.confirm_title'),
        {
          description: this.intl.t('content_pillars.ideas.discard.confirm_message'),
          confirmButton: this.intl.t('content_pillars.ideas.discard.confirm_button'),
          cancelButton: this.intl.t('shared_words.cancel')
        }
      );

      if (!didConfirm) {
        return;
      }

      taskFor(this.deleteContentIdea).linked().perform();

      this.segment.track(SegmentEventTypes.DeletedContentIdea, {
        best_describes: this.auth.currentAccount.bestDescribes ?? '',
        content_idea_id: this.args.contentIdea.id
      });
      this.alerts.success(this.intl.t('content_pillars.ideas.discard.success'));
    } catch (error) {
      this.errors.log('hideContentIdea Error', error);
    }
  }

  @action
  refineContentIdea(): void {
    const refineContentIdeaCacheKey = `${this.auth.currentUserModel.id}_refine_content_idea_triggered`;

    this.segment.track(SegmentEventTypes.ClickedRefineContentIdea, {
      location: 'content pillars page'
    });

    this.cache.add(refineContentIdeaCacheKey, true, { expiry: this.cache.maxExpiryDate(), persist: true });

    this.alerts.info(this.intl.t('alerts.content_pillars.ideas.refine_idea_description'), {
      title: this.intl.t('alerts.content_pillars.ideas.refine_idea_title')
    });
  }

  @task
  *saveContentIdea(event: MouseEvent | KeyboardEvent): TaskGenerator<void> {
    event.preventDefault();

    if (!this.contentIdeaChangeset.isValid) {
      return;
    }

    try {
      yield this.contentIdeaChangeset.save();
      this.isEditing = false;
    } catch (error) {
      this.errors.log('saveContentIdea Error', error);
    }
  }

  @task
  *setContentIdeaUsed(): TaskGenerator<void> {
    try {
      this.contentIdeaChangeset.used = true;
      yield this.contentIdeaChangeset.save();
    } catch (error) {
      this.errors.log('setContentIdeaUsed Error', error);
    }
  }
}
